import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { USER_ID_PROVIDER } from '@zag/ui';
import type { TranslatableText } from '@zag/core/common';
import type { FeatureKey } from '@zag/features/common';

/**
 * Background polling cadence for feature-licence changes. Licence toggles in
 * the control plane already push an HMAC-signed refresh to the tenant API
 * (see LFF-5), but that only invalidates the SERVER cache. The browser still
 * holds its own resolved snapshot until we re-fetch — this interval bounds
 * the propagation delay to a user's already-open tab.
 */
const REFRESH_POLL_MS = 30_000;
const FOCUS_REFRESH_MIN_GAP_MS = 60_000;

export interface FeatureView {
  key: string;
  name: string;
  description?: string | null;
  translations?: TranslatableText<'name' | 'description'>;
}

export interface FeaturesResponse {
  features: FeatureKey[];
  effectivePermissions: string[];
  licences: {
    key: string;
    name: string;
    validFrom: string | null;
    validTo: string | null;
    description?: string | null;
    translations?: TranslatableText<'name' | 'description'>;
    features: FeatureView[];
  }[];
  flags: { key: string; enabled: boolean; appliesToMe: boolean; features: FeatureKey[] }[];
}

/**
 * Client-side view of the tenant's active features, licences, feature flags
 * and the user's effective permission set (intersection of RBAC permissions
 * with active features — permissions whose feature_key is not in the active
 * set are filtered out server-side).
 *
 * Fail-open policy: when the resource has not yet resolved OR has errored,
 * hasFeature/hasPermission return `true` so consumers don't flash the wrong
 * UI on a transient network blip. The server-side FeatureGuard remains the
 * authoritative enforcement — the UI just reduces noise.
 */
@Injectable({ providedIn: 'root' })
export class FeatureAccessClientService {
  private readonly http = inject(HttpClient);
  private readonly reloadTrigger = signal(0);
  private readonly lastResolvedSnapshot = signal<FeaturesResponse | undefined>(undefined);
  private readonly userIdFn = inject(USER_ID_PROVIDER, { optional: true });
  private readonly currentUserId = computed(() => this.userIdFn?.() ?? '__anon__');

  readonly featuresResource = rxResource<FeaturesResponse, { trigger: number }>({
    params: () => ({ trigger: this.reloadTrigger() }),
    stream: () => this.http.get<FeaturesResponse>('/api/v1/features'),
  });

  private readonly snapshot = computed(() =>
    this.featuresResource.hasValue() ? this.featuresResource.value() : this.lastResolvedSnapshot()
  );

  private readonly destroyRef = inject(DestroyRef);
  private lastSuccessfulRefreshAt = 0;

  constructor() {
    effect(() => {
      if (this.featuresResource.hasValue()) {
        this.lastResolvedSnapshot.set(this.featuresResource.value());
        this.lastSuccessfulRefreshAt = Date.now();
      }
    });

    // Background polling — bounds the propagation delay for licence /
    // feature-flag changes made in the control plane while a user's tab
    // stays open.
    const intervalId = setInterval(() => this.refresh(), REFRESH_POLL_MS);

    // Immediate refresh whenever the user returns to the tab — closes the
    // "long-idle browser" gap that polling alone would leave. Skipped when a
    // refresh resolved recently so focus flapping can't refetch-storm the shell.
    const onFocus = (): void => this.refreshIfStale();
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') this.refreshIfStale();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    this.destroyRef.onDestroy(() => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    let previousUserId: string | undefined;
    effect(() => {
      const userId = this.currentUserId();
      if (previousUserId === undefined) {
        previousUserId = userId;
        return;
      }
      if (previousUserId === userId) return;
      previousUserId = userId;
      untracked(() => {
        this.lastResolvedSnapshot.set(undefined);
        this.refresh();
      });
    });
  }

  private readonly featureSet = computed(() => {
    const value = this.snapshot();
    return value === undefined ? undefined : new Set<FeatureKey>(value.features);
  });

  private readonly permissionSet = computed(() => {
    const value = this.snapshot();
    return value === undefined ? undefined : new Set<string>(value.effectivePermissions);
  });

  private readonly flagsAppliesToMe = computed(() => {
    const value = this.snapshot();
    if (value === undefined) return undefined;
    return new Set<string>(value.flags.filter(f => f.enabled && f.appliesToMe).map(f => f.key));
  });

  /** Full snapshot from the server, or `undefined` until first resolution. */
  readonly features = computed(() => this.snapshot()?.features);
  readonly licences = computed(() => this.snapshot()?.licences);
  readonly flags = computed(() => this.snapshot()?.flags);
  readonly effectivePermissions = computed(() => this.snapshot()?.effectivePermissions);

  /**
   * True once the server response has resolved. Passive consumers (nav hiding,
   * route guards) fail open via {@link hasFeature} while unknown, but a
   * consumer that triggers a *network side-effect* for a gated feature should
   * wait for this so it doesn't fire against a tenant that lacks the licence.
   */
  readonly featuresLoaded = computed(() => this.snapshot() !== undefined);

  /**
   * True when active — or while unknown (fail open, server enforces).
   *
   * Accepts either a fine-grained FeatureKey (`'timetracking.clock-in'`) or a
   * ModuleKey (`'timetracking'`). For a ModuleKey, returns true iff ANY feature
   * whose key starts with `<moduleKey>.` is active. Existing route data
   * (`data.feature: 'timetracking' | 'planning' | 'documents' | ...`) keeps
   * working; new routes can be surgical with a fine-grained key.
   */
  hasFeature(feature: FeatureKey | string): boolean {
    const features = this.featureSet();
    if (features === undefined) return true;
    if (features.has(feature as FeatureKey)) return true;
    if (feature.includes('.')) return false;
    const prefix = feature + '.';
    for (const f of features) {
      if (f.startsWith(prefix)) return true;
    }
    return false;
  }

  /** True when the user has this permission AND its owning feature is active. */
  hasPermission(permission: string): boolean {
    const perms = this.permissionSet();
    return perms === undefined || perms.has(permission);
  }

  /** True when a feature flag is enabled AND currently applies to the calling user. */
  isFlagActive(flagKey: string): boolean {
    const flags = this.flagsAppliesToMe();
    return flags === undefined || flags.has(flagKey);
  }

  /** Re-fetch after a FEATURE_NOT_LICENSED response so stale navigation disappears. */
  refresh(): void {
    this.reloadTrigger.update(count => count + 1);
  }

  private refreshIfStale(): void {
    if (Date.now() - this.lastSuccessfulRefreshAt < FOCUS_REFRESH_MIN_GAP_MS) return;
    this.refresh();
  }
}
